import React, { FC } from "react";
import { Task } from "@/types";
import Drawer from "@/components/molecules/DrawerTasks";
import Spinner from "@/components/atoms/Spinner/Spinner";
import { Container } from "@mantine/core";
import FinalAmountMultipleTask from "@/components/organisms/TaskDrawer/Tasks/FinalAmountMultipleTask";
import { useGetOrderById, useGetOrderByTransactionId } from "@/services/Orders";
import TaskTypeEnum from "@/constants/enums/TaskTypeEnum";
import FullLoadTransportTask from "@/components/organisms/TaskDrawer/Tasks/FullLoadTransportTask";
import { useAuth } from "@/contexts/AuthContext";
import CollectiveInvoicingTask from "@/components/organisms/TaskDrawer/Tasks/CollectiveInvoicingTask";
import { CreditRequestTask } from "./Tasks/CreditRequestTask";
import FinalAmountTask from "./Tasks/FinalAmountTask";
import ContractTask from "@/components/organisms/TaskDrawer/Tasks/ContractTask";
import CollectionTask from "@/components/organisms/TaskDrawer/Tasks/CollectionTask";
import DeviationConfirmationTask from "@/components/organisms/TaskDrawer/Tasks/DeviationConfirmationTask";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  isLoading: boolean;
  task?: Task;
  setSelectedTask?: (task: Task) => void;
}

const TaskDrawer: FC<Props> = (props) => {
  const { isOpen, onClose, isLoading, task } = props;
  const { currentCompany, user } = useAuth();

  const { data: orderByTransaction } = useGetOrderByTransactionId(
    task?.connected,
    currentCompany?.companyId as number,
    {
      enabled:
        (!!task?.connected && task.type === TaskTypeEnum.TRANSPORT_SELECTION) || task?.type === TaskTypeEnum.FEEDBACK,
    }
  );
  const { data: orderById } = useGetOrderById(
    task?.type === TaskTypeEnum.COLLECTIVE_INVOICING ? (task.orderIds[0] as number) : (task?.connected as number),
    undefined,
    user?.language.id,
    {
      enabled:
        (!!task?.connected && task.type === TaskTypeEnum.AUCTION_END_PAYMENT) ||
        task?.type === TaskTypeEnum.FINAL_AMOUNT ||
        task?.type === TaskTypeEnum.AUCTION_END_TRANSPORT ||
        task?.type === TaskTypeEnum.COLLECTIVE_INVOICING,
    }
  );

  const order = orderById || orderByTransaction;

  const renderContent = (task: Task) => {
    const type: TaskTypeEnum = task.type as TaskTypeEnum;
    switch (type) {
      case TaskTypeEnum.CREDIT_REQUEST:
        return <CreditRequestTask task={task} onClose={onClose} />;
      case TaskTypeEnum.APPROVE_CONTRACT:
        return <ContractTask task={task} contractId={task.connected} onClose={onClose} type={"approve"} />;
      case TaskTypeEnum.ARCHIVE_CONTRACT:
        return <ContractTask task={task} contractId={task.connected} onClose={onClose} type={"archive"} />;
      case TaskTypeEnum.CONTRACT_EXPIRING:
        return <ContractTask task={task} contractId={task.connected} onClose={onClose} type={"expire"} />;
      case TaskTypeEnum.FINAL_AMOUNT:
        return <FinalAmountTask task={task} onClose={onClose} order={order} />;
      case TaskTypeEnum.FINAL_AMOUNT_MULTIPLE:
        return <FinalAmountMultipleTask task={task} onClose={onClose} />;
      case TaskTypeEnum.TRANSPORT_SELECTION:
        return <FullLoadTransportTask transactionId={task.connected} onClose={onClose} />;
      case TaskTypeEnum.COLLECTION:
        return <CollectionTask task={task} onClose={onClose} />;
      case TaskTypeEnum.DEVIATION_CONFIRMATION:
        return <DeviationConfirmationTask task={task} onClose={onClose} />;
      case TaskTypeEnum.COLLECTIVE_INVOICING:
        return (
          <CollectiveInvoicingTask
            task={task}
            order={order}
            currentCompanyId={currentCompany?.companyId}
            onClose={onClose}
          />
        );

      // Outdated — intentionally render nothing.
      case TaskTypeEnum.FEEDBACK:
      case TaskTypeEnum.AUCTION_END_TRANSPORT:
      case TaskTypeEnum.AUCTION_END_PAYMENT:
        return null;

      // Routed to DrawerOrders via TasksTable — never reached here, listed for exhaustiveness.
      case TaskTypeEnum.APPROVE_ORDER:
      case TaskTypeEnum.PROCESS_ORDER:
        return null;

      // No drawer implemented yet — listed explicitly so adding a new TaskTypeEnum
      // value fails the exhaustiveness check below until it is handled here.
      case TaskTypeEnum.CLAIM:
      case TaskTypeEnum.FINAL_WEIGHT:
      case TaskTypeEnum.REGISTER_SERVICE_VALUE:
        return null;

      default: {
        const _exhaustive: never = type;
        return _exhaustive;
      }
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose}>
      <Spinner loading={isLoading} />
      {task && (
        <Container p={0} w={"100%"} h={"100%"}>
          {renderContent(task)}
        </Container>
      )}
    </Drawer>
  );
};

// interface BuildGeneralContentProps {
//   task: Task;
//   order: Order | undefined;
//   companyCurrency: CurrencyCode;
// }
//
// const BuildGeneralContent: React.FC<BuildGeneralContentProps> = ({ task, order, companyCurrency }) => {
//   const { t } = useTranslation();
//   if (!order) return <Text>{t("tasks:noOrderFound")}</Text>;
//   return (
//     <>
//       <Divider />
//       <Group justify="space-between">
//         <Text size="md" c={"dimmed"}>
//           {t("common:date")}
//         </Text>
//         <Text size="md">{dayjs(order.created).format("DD.MM.YYYY")}</Text>
//       </Group>
//
//       <Group justify="space-between">
//         <Text size="md" c={"dimmed"}>
//           {t("tasks:counterParty")}
//         </Text>
//         <Text size="md">{task.counterParty}</Text>
//       </Group>
//
//       <Group justify="space-between">
//         <Text size="md" c={"dimmed"}>
//           {t("common:type")}
//         </Text>
//         <Text size="md">{t("enumerables:plastic." + order.salesItem.salesItem)}</Text>
//       </Group>
//
//       <Group justify="space-between">
//         <Text size="md" c={"dimmed"}>
//           {t("common:amount")}
//         </Text>
//         <Text size="md">
//           {task.type === TaskTypeEnum.FINAL_AMOUNT
//             ? calculateDeviation(task.amount, order.amountDeviation)
//             : `${task.amount} ${order.unit.unit}`}
//         </Text>
//       </Group>
//
//       {order.price && (
//         <Group justify="space-between">
//           <Text size="md" c={"dimmed"}>
//             {t("common:price")}
//           </Text>
//           <Text size="md">
//             {order.price[companyCurrency as CurrencyCode]},- {companyCurrency} per {order.priceUnit?.unit}
//           </Text>
//         </Group>
//       )}
//     </>
//   );
// };

export default TaskDrawer;
